/* eslint-disable sonarjs/cognitive-complexity */
/* eslint-disable no-console */
import React, { useState, useRef, useCallback } from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  RACSelect,
  CircularProgress,
  RACButton,
  IconButton,
} from '@rentacenter/racstrap';
import { ResolutionField } from '../../../types/types';
import {
  useCustomerServiceDispatch,
  useCustomerServiceState,
} from '../../../context/CustomerService/CustomerServiceProvider';
import {
  attachmentsUrl,
  getTicketInfo,
  maganeIncident,
} from '../../../api/user';
import { customerStyles } from '../../customerStyles';
import { useUserStateContext } from '../../../context/user/user-contexts';
import {
  buildUploadPayload,
  gridStyle,
  readFileAsBase64,
  removeEmptyFields,
  uniqueifyFilenames,
} from '../../shared/utils';
import { ReactComponent as Attachment } from '../../../assets/images/attachment-icon.svg';
import { useReferenceContext } from '../../../context/CustomerService/ReferenceProvider';
import CustomTextArea from '../../shared/CustomTextArea';
import { KeyboardArrowUp, KeyboardArrowDown } from '@material-ui/icons';

const Resolnformation: React.FC = () => {
  const classes = customerStyles();
  const { incident } = useCustomerServiceState();
  const { references, loading } = useReferenceContext();
  const { user } = useUserStateContext();
  const { updateIncidentSection } = useCustomerServiceDispatch();
  const [screenLoader, setScreenLoader] = useState<boolean>(false);
  const [displaySearch, setDisplaySearch] = useState<boolean>(true);

  const { resolution } = incident;
  const rscode = resolution.rscode;
  const rsnote = resolution.rsnote?.trim();

  const handleChange = (name: ResolutionField, value: string) => {
    updateIncidentSection('resolution', { [name]: value });
  };

  const fileInputRef = useRef<any>(null);

  const handleSubmit = async () => {
    try {
      setScreenLoader(true);
      const payload: Record<string, any> = {
        ticketNumber: incident.basicInfo.ticketNumber ?? '',
        resolutionType: rscode ?? '',
        resolvedBy: user?.employeeId ?? '',
        resolvedByName: `${user?.firstName ?? ''} ${
          user?.lastName ?? ''
        }`.trim(),
        resolvedByRole: user?.role ?? '',
        status: 'RSLV',
        resolutionNotes: rsnote ?? '',
      };
      const cleanedPayload = removeEmptyFields(payload);
      const res = await maganeIncident(cleanedPayload);
      if (res?.ticketId && res?.ticketNumber) {
        updateIncidentSection('resolution', { resolved: true });
        const response = await getTicketInfo({
          ticketId: res?.ticketNumber,
        });
        if (response && response.cmsTicketId) {
          updateIncidentSection('activity', {
            businessDuration: response.businessDuration,
            activities: response.activities,
            ticketNotes: response.ticketNotes,
          });
          setScreenLoader(false);
        }
      }
    } catch (error) {
      setScreenLoader(false);
      console.error('Error submitting ticket:', error);
    }
  };

  const resolved = incident.resolution.resolved;
  const rscodes = incident.resolution.rscode;

  const isSubmitEnabled = !resolved && rscodes !== 'NONE';

  const handleButtonClick = () => {
    fileInputRef?.current?.click();
  };

  const handleFileUpload = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files;
      if (!files || files.length === 0) return;

      const existingAttachments = incident.basicInfo.attachments ?? [];
      const uniqueFiles = uniqueifyFilenames(
        Array.from(files),
        existingAttachments.map((a) => a.attachmentName)
      );

      try {
        setScreenLoader(true);
        const payload = await buildUploadPayload(uniqueFiles, readFileAsBase64);
        const result = await attachmentsUrl(payload);

        if (result && Array.isArray(result.attachments)) {
          const formattedAttachments = result.attachments.map(
            (item: { documentName: string; documentLocation: string }) => ({
              attachmentName: item.documentName,
              attachmentLocation: item.documentLocation,
            })
          );

          // Attaching the doc to incident
          const incidentPayload: Record<string, any> = {
            ticketNumber: incident.basicInfo.ticketNumber ?? '',
            attachments: formattedAttachments.map(
              (attachment: {
                attachmentName: any;
                attachmentLocation: any;
              }) => ({
                actionType: 'ADD',
                documentName: attachment.attachmentName,
                documentLocation: attachment.attachmentLocation,
              })
            ),
          };

          const res = await maganeIncident(incidentPayload);
          if (res?.ticketId && res?.ticketNumber) {
            const response = await getTicketInfo({
              ticketId: res?.ticketNumber,
            });
            if (response && response.cmsTicketId) {
              updateIncidentSection('basicInfo', {
                attachments: response.attachments,
              });
              updateIncidentSection('activity', {
                businessDuration: response.businessDuration,
                activities: response.activities,
                ticketNotes: response.ticketNotes,
              });
            }
          }
        } else {
          console.warn('Unexpected response format:', result);
        }
      } catch (error) {
        console.error('Failed to upload and store attachments:', error);
      } finally {
        setScreenLoader(false);
        if (event.target) event.target.value = '';
      }
    },
    [
      incident.basicInfo.attachments,
      incident.basicInfo.ticketNumber,
      updateIncidentSection,
    ]
  );
  const toggleFilterPanel = () => {
    const next = !displaySearch;
    setDisplaySearch(next);
  };
  return (
    <>
      {screenLoader && (
        <Grid className={classes.masterLoader}>
          <Grid className={classes.Loader}>
            <CircularProgress />
          </Grid>
        </Grid>
      )}

      <Grid
        container
        id="resolinfo"
        className={`${classes.mb16} ${classes.px2}`}
      >
        <Grid item md={12}>
          <Card className={`${classes.card} ${classes.w100}`}>
            <CardContent>
              <Grid container spacing={2}>
                <Grid item md={6}>
                  <Typography className={classes.title}>
                    Resolution Information
                  </Typography>
                </Grid>
                <Grid
                  item
                  md={6}
                  container
                  justifyContent="flex-end"
                  alignItems="flex-start"
                >
                  <IconButton onClick={toggleFilterPanel} size="small">
                    {displaySearch ? (
                      <KeyboardArrowUp />
                    ) : (
                      <KeyboardArrowDown />
                    )}
                  </IconButton>
                </Grid>
              </Grid>
              {displaySearch && (
                <Grid container spacing={3} justifyContent="space-between">
                  <Grid item lg={4} md={4} sm={4} style={gridStyle}>
                    <RACSelect
                      loading={loading}
                      name={'rscode'}
                      inputLabel={'Resolution codes'}
                      required
                      options={references.resolution_type ?? []}
                      defaultValue={incident.resolution.rscode ?? ''}
                      onChange={(e: any) =>
                        handleChange('rscode', e.target.value)
                      }
                      isDisabled={resolved}
                    />
                  </Grid>

                  <Grid item lg={4} md={4} sm={4} style={gridStyle}>
                    <Typography className={classes.notesLabel}>
                      {'Resolution notes'}{' '}
                    </Typography>
                    <CustomTextArea
                      value={incident.resolution.rsnote ?? ''}
                      onChange={(e: any) => handleChange('rsnote', e)}
                      minHeight={100}
                      maxHeight={300}
                      disabled={resolved}
                    />
                  </Grid>

                  <Grid item lg={4} md={4} sm={4} style={gridStyle}>
                    <>
                      <RACButton
                        variant="outlined"
                        color="secondary"
                        id="uploadDocument"
                        className={`${classes.uploadButton} ${classes.uploadBtnRes}`}
                        onClick={handleButtonClick}
                        startIcon={<Attachment />}
                        disabled={resolved}
                      >
                        Add Attachments
                        <input
                          ref={fileInputRef}
                          multiple
                          type="file"
                          onChange={handleFileUpload}
                          accept=".pdf,image/*,.txt,.xlsx,.xls,.doc,.docx,.mp4,.avi"
                          style={{ display: 'none' }}
                        />
                      </RACButton>
                      <RACButton
                        className={classes.footerButtonRes}
                        variant="contained"
                        size="small"
                        color="primary"
                        disabled={!isSubmitEnabled}
                        onClick={handleSubmit}
                      >
                        Mark as Resolved
                      </RACButton>
                    </>
                  </Grid>
                </Grid>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </>
  );
};

export default Resolnformation;
